import React, { useState, useEffect } from 'react';
import { X, Database } from 'lucide-react';
import { dataSourcesAPI } from '../../services/api';
import { DataSource } from '../../types';
import toast from 'react-hot-toast';

interface DataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataSource?: DataSource | null;
  onSuccess: () => void;
}

const DataSourceModal: React.FC<DataSourceModalProps> = ({
  isOpen,
  onClose,
  dataSource,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!dataSource;

  useEffect(() => {
    if (isOpen) {
      if (dataSource) {
        setFormData({
          name: dataSource.name,
          description: dataSource.description || '',
          is_active: dataSource.is_active
        });
      } else {
        setFormData({
          name: '',
          description: '',
          is_active: true
        });
      }
    }
  }, [isOpen, dataSource]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Data source name is required');
      return;
    }

    if (formData.name.trim().length < 3) {
      toast.error('Data source name must be at least 3 characters long');
      return;
    }

    if (formData.description.trim().length < 10) {
      toast.error('Description must be at least 10 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing && dataSource) {
        await dataSourcesAPI.updateDataSource(dataSource.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          is_active: formData.is_active
        });
        toast.success('Data source updated successfully');
      } else {
        await dataSourcesAPI.createDataSource({
          name: formData.name.trim(),
          description: formData.description.trim(),
          is_active: formData.is_active
        });
        toast.success('Data source created successfully');
      }
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/admin/batch');
      cacheService.clearPattern('/dashboard/batch');
      
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} data source`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Database className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Edit Data Source' : 'Create Data Source'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Data Source Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Clinical Database, Survey Data"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
              required
              minLength={3}
              maxLength={255}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 3 characters, maximum 255 characters</p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe what type of data this source contains and how it's used..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors resize-none"
              required
              minLength={10}
              maxLength={1000}
            />
            <p className="text-xs text-gray-500 mt-1">Minimum 10 characters, maximum 1000 characters</p>
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleCheckboxChange}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
              Active (available for selection in publications)
            </label>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                </div>
              ) : (
                isEditing ? 'Update Data Source' : 'Create Data Source'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DataSourceModal;
